import { AxiosError } from "axios";
import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import { handleApiError } from "@/lib/utils";
import adminAxios from "@/lib/axios";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface IProgramFeeV2MetadataItem {
  id?: string;
  label: string;
  value?: string;
  icon?: string;
  icon_alt?: string;
  description?: string;
  text_color?: string;
  value_text_color?: string;
  value_font_size?: string;
  is_total?: boolean;
  priority?: number;
  status?: boolean;
}

export interface IProgramFeeV2Category {
  id?: string;
  title: string;
  text_color?: string;
  bg_color?: string;
  active_text_color?: string;
  active_bg_color?: string;
  border_color?: string;
  order_index?: number;
  metadata: IProgramFeeV2MetadataItem[];
}

export interface IProgramFeeV2 {
  id?: number;
  page_id?: number;
  component_id?: number;

  // Title section
  title_1?: string;
  title_1_color?: string;
  title_2?: string;
  title_2_gradient_from?: string;
  title_2_gradient_to?: string;
  title_2_gradient_angle?: string;

  // Fee table gradient
  table_gradient_from?: string;
  table_gradient_to?: string;
  table_gradient_angle?: string;

  // What You Get section
  metadata_title?: string;
  metadata_title_color?: string;
  metadata?: IProgramFeeV2MetadataItem[];

  // Feature cards
  metadata_1?: IProgramFeeV2MetadataItem[];

  // Categories (tabs with fee data)
  categories?: IProgramFeeV2Category[];

  // CTA
  cta_text?: string;
  cta_text_color?: string;
  cta_gradient_from?: string;
  cta_gradient_to?: string;
  cta_gradient_angle?: string;
  cta_icon?: string;
  cta_icon_alt?: string;

  // Background gradient variants
  backvariant?: string;
  gradient_color_1?: string;
  gradient_color_2?: string;
  gradient_color_3?: string;
  gradient_color_4?: string;

  // General
  background_color?: string;
  status?: boolean;

  // Audit
  created_by?: number;
  updated_by?: number;
}

// ─── API Methods ────────────────────────────────────────────────────────────

export async function createProgramFeeV2(payload: Partial<IProgramFeeV2>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE_V2.CREATE,
      payload
    );
    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateProgramFeeV2(
  id: number,
  payload: Partial<IProgramFeeV2>
) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE_V2.UPDATE(id),
      payload
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getProgramFeeV2ByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE_V2.GET_BY_COMPONENT_ID(componentId)
    );
    return response?.data;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getProgramFeeV2DataByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_FEE_V2.GET_DATA_BY_COMPONENT_ID(componentId)
    );
    return response?.data;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}
